// GenericAccordion.tsx
import React, { useRef, useEffect } from 'react';
import '../Asthma.scss';

export interface AccordionItem {
  key: string;
  title: string;
  content: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

interface GenericAccordionProps {
  items: AccordionItem[];
}

export const GenericAccordion: React.FC<GenericAccordionProps> = ({ items }) => {
  const itemRefs = useRef<{ [key: string]: HTMLLIElement | null }>({});
  const prevOpenKey = useRef<string | null>(null);

  useEffect(() => {
    // Find the currently open item
    const openItem = items.find(item => item.isOpen);
    const currentOpenKey = openItem ? openItem.key : null;

    if (currentOpenKey && currentOpenKey !== prevOpenKey.current) {
      const el = itemRefs.current[currentOpenKey];
      if (el) {
        // Wait a brief moment to allow the UI to start expanding
        // Then scroll smoothly and elegantly so it's not a jarring jump
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }
    }
    prevOpenKey.current = currentOpenKey;
  }, [items]);

  return (
    <div className="scoring-guide-card">
      <ul className="scoring-guide-list">
        {items.map((item) => (
          <li 
            key={item.key}
            // Keep a nice margin from the top edge (the fixed header)
            style={{ scrollMarginTop: '100px' }}
            ref={(el) => { itemRefs.current[item.key] = el; }}
          >
            <div className={`asthma-accordion-item ${item.isOpen ? 'open' : ''}`}>
              <div
                className="asthma-accordion-header"
                onClick={item.onToggle}
              >
                <span className="asthma-accordion-header-title">
                  {item.title}
                </span>
                <span className="asthma-accordion-header-arrow">
                  <span style={{
                    display: 'inline-block',
                    transform: item.isOpen ? 'rotate(180deg)' : 'rotate(270deg)',
                    transition: 'transform 0.2s'
                  }}>
                    ‹
                  </span>
                </span>
              </div>

              {item.isOpen && (
                <div className="asthma-accordion-panel">
                  {item.content}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};